/**
 * canvas-engine/presets/presetData/chromePresets.js
 *
 * TRANSCRIBED Chrome_Section (Header / Footer) default VALUES.
 *
 * ZERO-REUSE CONSTRAINT (Task 3.1): imports NOTHING. Header and footer default
 * values (logo, nav, social, icons, colors, spacing) are transcribed from the
 * Default_Source header/footer definitions of every domain
 * (`educationHeaderDefaults`/`educationFooterDefaults`,
 * `waasHeaderDefaults`/`waasFooterDefaults`,
 * `flexOfficeHeaderDefaults`/`flexOfficeFooterDefaults`,
 * `retailHeaderDefaults`/`retailFooterDefaults`) as plain DATA.
 * No React, no rendering, no side effects.
 *
 * The chrome adapter (Task 3.3 `chrome.js`) decomposes these into individually
 * editable logo / nav / social / icon Canvas_Elements (Req 4.2, 5.7).
 *
 * Descriptor shape adds chrome-specific content groups:
 *   content: { logo: { asset, alt, width }, nav: [...], social: [...],
 *              links: {...grouped link lists}, contact: {...}, ...labels }
 *
 * Requirements: 3.6, 3.7, 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 5.7
 */

const img = (url = '') => ({ storedLink: '', displayLink: url });

const EDUCATION_BASE =
  'https://krumago-application-assets.s3.us-east-1.amazonaws.com/Krumago+Main+Assets/';
const EDUCATION_LOGO = `${EDUCATION_BASE}AVPS.png.png`;
const KRUMAGO_LOGO = `${EDUCATION_BASE}Krumagoimg.png`;

/**
 * CHROME_PRESETS — header/footer default descriptors keyed by domain.
 * Each domain exposes `header` and `footer` descriptors. A neutral `generic`
 * domain provides the fallback used by non-family templates.
 */
export const CHROME_PRESETS = {
  // ── Generic / fallback chrome ──
  generic: {
    header: {
      sectionType: 'header',
      label: 'Header',
      domain: 'generic',
      style: { background: '#ffffff', textColor: '#1f2937', borderColor: '#f1f5f9', shadow: '0 2px 10px rgba(0,0,0,0.08)', height: '70px' },
      layout: { align: 'left', width: 'full', sticky: true },
      content: {
        logo: { asset: img(''), alt: 'Logo', width: '120px' },
        brandName: 'Brand',
        nav: [
          { label: 'Home', route: '/', visible: true },
          { label: 'About', route: '/about', visible: true },
          { label: 'Contact', route: '/contact', visible: true },
        ],
        signInLabel: 'Sign In',
        cartLabel: 'Cart',
      },
    },
    footer: {
      sectionType: 'footer',
      label: 'Footer',
      domain: 'generic',
      style: { background: '#f3f4f6', textColor: '#374151', headingColor: '#1f2937', linkColor: '#374151' },
      layout: { align: 'left', width: 'full', columns: 4 },
      content: {
        copyrightName: 'Brand',
        quickLinksTitle: 'Quick Links',
        socialTitle: 'Follow Us',
        legalTitle: 'Legal',
        contact: { phone: '', email: '' },
        social: [
          { label: 'Facebook', route: '#', icon: 'Facebook', visible: true },
          { label: 'Instagram', route: '#', icon: 'Instagram', visible: true },
          { label: 'Twitter', route: '#', icon: 'Twitter', visible: true },
        ],
        legalLinks: [
          { label: 'Privacy Policy', route: '/policy/privacy_policy', visible: true },
          { label: 'Terms & Conditions', route: '/policy/terms_conditions', visible: true },
        ],
      },
    },
  },

  // ── Education chrome (transcribed) ──
  education: {
    header: {
      sectionType: 'header',
      label: 'Education header',
      domain: 'education',
      style: { background: '#ffffff', textColor: '#1f2937', borderColor: '#f1f5f9', shadow: '0 2px 10px rgba(0, 0, 0, 0.08)', height: '70px' },
      layout: { align: 'left', width: 'full', sticky: true },
      content: {
        logo: { asset: img(EDUCATION_LOGO), alt: 'AVPS Academy', width: '100px' },
        searchPlaceholder: 'Search courses, categories...',
        searchButtonLabel: 'Search',
        signInLabel: 'Sign In',
        accountLabel: 'Account',
        cartLabel: 'Cart',
        nav: [
          { label: 'Home', route: '/', visible: true },
          { label: 'Courses', route: '/course', visible: true },
          { label: 'About', route: '/about', visible: true },
          { label: 'Contact', route: '/contact', visible: true },
        ],
      },
    },
    footer: {
      sectionType: 'footer',
      label: 'Education footer',
      domain: 'education',
      style: { background: '#f3f4f6', textColor: '#374151', headingColor: '#1f2937', linkColor: '#374151' },
      layout: { align: 'left', width: 'full', columns: 4 },
      content: {
        copyrightName: 'AVPS Academy',
        socialTitle: 'Follow Us',
        quickLinksTitle: 'Quick Links',
        contactTitle: 'Contact',
        legalTitle: 'Legal',
        contact: { phone: '+1 244-5333-7783', email: 'avpstech.com' },
        quickLinks: [
          { label: 'Help', route: '#', visible: true },
          { label: 'Share', route: '#', visible: true },
        ],
        social: [
          { label: 'Facebook', route: '#', icon: 'Facebook', visible: true },
          { label: 'Instagram', route: '#', icon: 'Instagram', visible: true },
          { label: 'Twitter', route: '#', icon: 'Twitter', visible: true },
          { label: 'YouTube', route: '#', icon: 'Youtube', visible: true },
        ],
        legalLinks: [
          { label: 'Privacy Policy', route: '/policy/privacy_policy', visible: true },
          { label: 'Terms & Conditions', route: '/policy/terms_conditions', visible: true },
        ],
        poweredByText: 'Powered by',
        poweredByLogo: img(KRUMAGO_LOGO),
        poweredByUrl: 'https://www.krumago.com',
        poweredByLabel: 'www.krumago.com',
      },
    },
  },

  // ── WAAS chrome (transcribed) ──
  waas: {
    header: {
      sectionType: 'header',
      label: 'WaaS header',
      domain: 'waas',
      style: { background: 'transparent', backgroundGradient: 'linear-gradient(180deg, rgba(8, 30, 42, .24), rgba(8, 30, 42, 0))', borderColor: 'transparent', shadow: 'none', height: '48px', maxWidth: '1200px', brandColor: '#ffffff' },
      layout: { align: 'right', width: 'full', sticky: true, overlay: true },
      content: {
        logo: { asset: img(''), alt: 'Company logo', width: '120px' },
        brandInitial: 'A',
        brandName: 'AVPS Tech',
        ctaLabel: 'Contact us',
        ctaUrl: '/contactus',
        ctaIcon: 'Calendar',
        signInLabel: 'Contact us',
        nav: [
          { label: 'Home', route: '/', icon: 'Home', visible: true },
          { label: 'Services', route: '/services', icon: 'Services', visible: true },
          { label: 'Server', route: '/server', icon: 'Cloud', visible: true },
          { label: 'About', route: '/about', icon: 'Info', visible: true },
          { label: 'Career', route: '/careers', icon: 'Briefcase', visible: true },
          { label: 'Contact Us', route: '/contactus', icon: 'Mail', visible: true },
          { label: 'Sign In', route: '/sign-in', icon: 'LogIn', visible: false },
          { label: 'Sign Up', route: '/sign-up', icon: 'UserPlus', visible: false },
        ],
      },
    },
    footer: {
      sectionType: 'footer',
      label: 'WaaS footer',
      domain: 'waas',
      style: { background: '#e6e8eb', textColor: '#4f5660', headingColor: '#0b0f16', linkColor: '#0b0f16' },
      layout: { align: 'left', width: 'full', columns: 3 },
      content: {
        newsletterTitle: 'Let us build the systems behind your next digital move.',
        newsletterPlaceholder: 'work@company.com',
        newsletterButtonLabel: 'START',
        newsletterConsentLabel: 'YES, CONTACT ME ABOUT IT SERVICES.',
        copyrightText: '(c) 2026 AVPS Tech. All rights reserved.',
        madeWithText: 'Software, cloud, servers, automation, and managed IT.',
        socialTitle: 'Social',
        quickLinksTitle: 'Company Info',
        legalTitle: 'Policies',
        contactTitle: 'Contact',
        contact: { phone: '+1 (555) 400-1020', email: 'hello@avpstech.com', address: 'Cloud and software delivery' },
        quickLinks: [
          { label: 'Services', route: '/services', icon: 'Services', visible: true },
          { label: 'Server', route: '/server', icon: 'Cloud', visible: true },
          { label: 'Careers', route: '/careers', icon: 'Briefcase', visible: true },
          { label: 'About', route: '/about', icon: 'Info', visible: true },
          { label: 'Contact', route: '/contactus', icon: 'Mail', visible: true },
          { label: 'Sign In', route: '/sign-in', icon: 'LogIn', visible: true },
          { label: 'Sign Up', route: '/sign-up', icon: 'UserPlus', visible: true },
        ],
        social: [
          { label: 'LinkedIn', route: '', icon: 'Linkedin', platformKey: 'linkedin', visible: true },
          { label: 'YouTube', route: '', icon: 'Youtube', platformKey: 'youtube', visible: true },
          { label: 'Facebook', route: '', icon: 'Facebook', platformKey: 'facebook', visible: true },
          { label: 'X', route: '', icon: 'Twitter', platformKey: 'twitter', visible: true },
        ],
        legalLinks: [
          { label: 'Refund Policy', route: '/policy/refund_policy', icon: 'Shield', visible: true },
          { label: 'Privacy Policy', route: '/policy/privacy_policy', icon: 'Shield', visible: true },
          { label: 'Terms & Conditions', route: '/policy/terms_conditions', icon: 'Shield', visible: true },
          { label: 'Accessibility Statement', route: '/policy/accessibility', icon: 'Shield', visible: true },
        ],
      },
    },
  },

  // ── FlexOffice chrome (transcribed) ──
  flexOffice: {
    header: {
      sectionType: 'header',
      label: 'FlexOffice header',
      domain: 'flexOffice',
      style: { background: '#ffffff', borderColor: '#f1f5f9', shadow: '0 8px 24px rgba(15, 23, 42, 0.08)', height: '64px' },
      layout: { align: 'left', width: 'full', sticky: true },
      content: {
        logo: { asset: img(''), alt: 'FlexOffice', width: '120px' },
        brandName: 'FlexOffice',
        ctaLabel: 'Inquire Now',
        ctaUrl: '/contact',
        signInLabel: 'Login',
        signUpLabel: 'Sign Up',
        nav: [
          { label: 'Home', route: '/', icon: 'Home', visible: true },
          { label: 'Features', route: '/our-features', icon: 'Star', visible: true },
          { label: 'Inquire Now', route: '/contact', icon: 'Help', highlight: true, visible: true },
        ],
      },
    },
    footer: {
      sectionType: 'footer',
      label: 'FlexOffice footer',
      domain: 'flexOffice',
      style: { background: '#0f172a', textColor: '#cbd5e1', headingColor: '#ffffff', linkColor: '#cac3f4' },
      layout: { align: 'left', width: 'full', columns: 4 },
      content: {
        socialTitle: 'Connect With Us',
        navigationTitle: 'Navigation',
        legalTitle: 'Legal Documents',
        contactTitle: 'Contact Information',
        copyrightText: 'All Rights Reserved.',
        poweredByText: 'Powered by',
        poweredByLabel: 'www.krumago.com',
        poweredByUrl: 'https://www.krumago.com',
        nav: [
          { label: 'Home', route: '/', visible: true },
          { label: 'Contact Us', route: '/contact', visible: true },
          { label: 'About Us', route: '/our-features', visible: true },
        ],
        legalLinks: [
          { label: 'Privacy Policy', route: '/privacy', visible: true },
          { label: 'Terms of Service', route: '/terms', visible: true },
          { label: 'Cookie Policy', route: '/cookies', visible: true },
          { label: 'Refund Policy', route: '/refund', visible: true },
        ],
        social: [
          { label: 'Facebook', route: 'https://www.facebook.com/', icon: 'Facebook', visible: true },
          { label: 'Twitter', route: 'https://twitter.com/', icon: 'Twitter', visible: true },
          { label: 'Instagram', route: 'https://www.instagram.com/', icon: 'Instagram', visible: true },
          { label: 'LinkedIn', route: 'https://www.linkedin.com/', icon: 'Linkedin', visible: true },
        ],
        contact: { address: '123 Workspace Ave, Suite 400, New York, NY 10001', phone: '+1 (555) 248-1010', email: 'hello@flexoffice.com' },
      },
    },
  },

  // ── Retail chrome (transcribed) ──
  retail: {
    header: {
      sectionType: 'header',
      label: 'Retail header',
      domain: 'retail',
      style: { background: '#ffffff', borderColor: '#e5e7eb', shadow: '0 8px 24px rgba(15, 23, 42, 0.10)', height: '80px' },
      layout: { align: 'left', width: 'full', sticky: true },
      content: {
        logo: { asset: img(''), alt: 'Retail Store', width: '120px' },
        brandName: 'Retail Store',
        searchPlaceholder: 'Search products, brands, categories',
        cartLabel: 'Cart',
        signInLabel: 'Sign In',
        nav: [
          { label: 'Home', route: '/', visible: true },
          { label: 'Fashion', route: '/category/Fashion', visible: true },
          { label: 'Home Decor', route: '/category/Home Decor', visible: true },
          { label: 'Electronics', route: '/category/Electronics', visible: true },
          { label: 'Beauty', route: '/category/Beauty', visible: true },
        ],
      },
    },
    footer: {
      sectionType: 'footer',
      label: 'Retail footer',
      domain: 'retail',
      style: { background: '#232f3e', textColor: '#ffffff', headingColor: '#ffffff', linkColor: '#f2f0ff' },
      layout: { align: 'left', width: 'full', columns: 4 },
      content: {
        onlineShoppingTitle: 'Online Shopping',
        usefulLinksTitle: 'Useful Links',
        policiesTitle: 'Customer Policies',
        socialTitle: 'Keep In Touch',
        copyrightText: 'All rights reserved.',
        contact: { phone: '+1 (678) 855-6990', email: 'system@avpstech.com' },
        categories: [
          { label: 'Fashion', route: '/category/Fashion', visible: true },
          { label: 'Home Decor', route: '/category/Home Decor', visible: true },
          { label: 'Electronics', route: '/category/Electronics', visible: true },
          { label: 'Beauty', route: '/category/Beauty', visible: true },
        ],
        usefulLinks: [
          { label: 'Blog', route: '#', visible: true },
          { label: 'Careers', route: '#', visible: true },
          { label: 'Site Map', route: '#', visible: true },
          { label: 'Corporate Information', route: '#', visible: true },
        ],
        legalLinks: [
          { label: 'Terms Of Use', route: '/termsConditions', visible: true },
          { label: 'Privacy Policy', route: '/privacyPolicy', visible: true },
          { label: 'User Acceptance', route: '/userAcceptance', visible: true },
          { label: 'Cookies', route: '/cookies', visible: true },
          { label: 'FAQ', route: '#', visible: true },
        ],
        social: [
          { label: 'Facebook', route: '#', icon: 'Facebook', visible: true },
          { label: 'Instagram', route: '#', icon: 'Instagram', visible: true },
          { label: 'LinkedIn', route: '#', icon: 'Linkedin', visible: true },
        ],
      },
    },
  },
};

export default CHROME_PRESETS;
